/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
export const PlaybackState = {
  IDLE: "idle",
  LOADING: "loading",
  PLAYING: "playing",
  PAUSED: "paused",
  BUFFERING: "buffering",
  SEEKING: "seeking",
  ENDED: "ended",
  ERROR: "error",
} as const;

export type PlaybackState = (typeof PlaybackState)[keyof typeof PlaybackState];

export interface ChromecastPlayerState {
  playbackState: PlaybackState;
  currentTime: number;
  duration: number;
  seekable: {
    start: number;
    end: number;
  };
  utcCurrentTime: number;
  utcDuration: number;
  utcSeekable: {
    start: number;
    end: number;
  };

  volume: number;

  subtitleTrack: chrome.cast.media.Track | null;
  subtitleTracks: chrome.cast.media.Track[];

  audioTrack: chrome.cast.media.Track | null;
  audioTracks: chrome.cast.media.Track[];

  qualityLevel: QualityLevel;
  qualityLevels: QualityLevel[];

  droppedFrames: number;
  bufferingEvents: number;

  hasStarted: boolean;
  isMuted: boolean;
  isLive: boolean;
  isSeekable: boolean;
  isAtLiveEdge: boolean;
}

export const AUTO_QUALITY_LEVEL_DEFINITION: QualityLevel = {
  id: "auto",
  label: "Auto",
  bitrate: 0,
  frameRate: 0,
  enabled: false,
};

export const DefaultPlayerState: ChromecastPlayerState = {
  playbackState: PlaybackState.IDLE,
  currentTime: 0,
  duration: 0,
  seekable: { start: 0, end: 0 },

  utcCurrentTime: 0,
  utcDuration: 0,
  utcSeekable: { start: 0, end: 0 },

  volume: 0,

  subtitleTrack: null,
  subtitleTracks: [],

  audioTrack: null,
  audioTracks: [],

  qualityLevel: AUTO_QUALITY_LEVEL_DEFINITION,
  qualityLevels: [],

  droppedFrames: 0,
  bufferingEvents: 0,

  hasStarted: false,
  isMuted: false,
  isLive: false,
  isSeekable: false,
  isAtLiveEdge: false,
};

export interface ChromecastEventHandlers {
  onConnected?: (isResumed: boolean) => void;
  onDisconnected?: () => void;
  onAvailabilityChange?: (available: boolean) => void;
  onStateChanged?: (
    newState: ChromecastPlayerState,
    oldState: ChromecastPlayerState,
  ) => void;
  onMuteChanged?: (isMuted: boolean) => void;
  onVolumeLevelChange?: () => void;
  onError?: (error: string) => void;
}
